import { GameOutput, PlayOutput } from '../generated-api';

/** Games not played for this many days count as dusty. */
export const DUSTY_AFTER_DAYS = 90;
/** A game needs at least this many plays before it counts as a former favourite. */
export const FAVOURITE_MIN_PLAYS = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DustyGame {
  game: GameOutput;
  /** 'forgotten': played a lot before, not lately. 'unplayed': owned but never played. */
  kind: 'forgotten' | 'unplayed';
  playCount: number;
  lastPlayedAt: string | null;
  daysSince: number | null;
}

export interface DustyOptions {
  now?: Date;
  /** Who's at the table: only count plays with at least one of them, and skip games too small for the table. */
  playerIds?: string[];
  /** Only games this owner has. */
  owner?: string | null;
}

const playerIdsOf = (play: PlayOutput): string[] => Object.values(play.teams || {}).flat();

/**
 * Former favourites that gathered dust (FAVOURITE_MIN_PLAYS+ plays, none in DUSTY_AFTER_DAYS),
 * most played first, then owned games nobody ever played, by name.
 */
export function computeDustyShelf(games: GameOutput[], plays: PlayOutput[], options: DustyOptions = {}): DustyGame[] {
  const now = options.now ?? new Date();
  const table = new Set(options.playerIds ?? []);
  const tableSize = table.size;

  const everPlayed = new Set<string>();
  const stats = new Map<string, { count: number; last: string }>();
  for (const play of plays) {
    everPlayed.add(play.gameId);
    if (tableSize > 0 && !playerIdsOf(play).some(id => table.has(id))) continue;
    const s = stats.get(play.gameId);
    if (!s) stats.set(play.gameId, { count: 1, last: play.playDate });
    else {
      s.count += 1;
      if (new Date(play.playDate) > new Date(s.last)) s.last = play.playDate;
    }
  }

  const forgotten: DustyGame[] = [];
  const unplayed: DustyGame[] = [];
  for (const game of games) {
    if (options.owner && !(game.owners || []).includes(options.owner)) continue;
    if (tableSize > 0 && tableSize < game.minTeams) continue;
    const s = stats.get(game.id);
    if (s) {
      const daysSince = Math.floor((now.getTime() - new Date(s.last).getTime()) / DAY_MS);
      if (s.count >= FAVOURITE_MIN_PLAYS && daysSince >= DUSTY_AFTER_DAYS) {
        forgotten.push({ game, kind: 'forgotten', playCount: s.count, lastPlayedAt: s.last, daysSince });
      }
    } else if (!everPlayed.has(game.id) && (game.owners || []).length > 0) {
      if (tableSize > 0 && tableSize > game.maxTeams) continue;
      unplayed.push({ game, kind: 'unplayed', playCount: 0, lastPlayedAt: null, daysSince: null });
    }
  }

  forgotten.sort((a, b) => b.playCount - a.playCount || (b.daysSince ?? 0) - (a.daysSince ?? 0));
  unplayed.sort((a, b) => a.game.name.localeCompare(b.game.name));
  return [...forgotten, ...unplayed];
}

/** "4 months", "1 year", … for how long a game has been gathering dust. */
export function dustAge(days: number): string {
  if (days >= 365) {
    const years = Math.floor(days / 365);
    return `${years} year${years === 1 ? '' : 's'}`;
  }
  const months = Math.max(1, Math.floor(days / 30));
  return `${months} month${months === 1 ? '' : 's'}`;
}
